import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_OG_IMAGE,
  DEFAULT_TWITTER_HANDLE,
  SITE_NAME,
  absoluteUrl,
  type SeoPageKey,
  SEO_PATHS,
} from '../config/seo';

type SeoHeadProps = {
  page: SeoPageKey;
  /** Override path when it differs from SEO_PATHS (rare). */
  path?: string;
  noIndex?: boolean;
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, extra?: Record<string, string>) {
  const selector = extra?.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  if (extra) {
    Object.entries(extra).forEach(([k, v]) => el!.setAttribute(k, v));
  }
}

/**
 * Updates document title, description, Open Graph, Twitter, canonical and html lang.
 * CRA has no react-helmet; this keeps marketing pages crawlable without new deps.
 */
export default function SeoHead({ page, path, noIndex = false }: SeoHeadProps) {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'en').split('-')[0];
  const resolvedPath = path ?? SEO_PATHS[page];
  const canonical = absoluteUrl(resolvedPath);
  const title = t(`seo.${page}.title`);
  const description = t(`seo.${page}.description`);
  const keywords = t(`seo.${page}.keywords`, { defaultValue: '' });

  useEffect(() => {
    document.title = title;
    document.documentElement.lang = locale === 'tr' ? 'tr' : 'en';

    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    upsertMeta('name', 'author', SITE_NAME);
    upsertMeta('name', 'application-name', SITE_NAME);

    upsertMeta('property', 'og:type', page === 'home' ? 'website' : 'article');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', DEFAULT_OG_IMAGE);
    upsertMeta('property', 'og:image:alt', `${SITE_NAME} — App Store & Play Store release platform`);
    upsertMeta('property', 'og:locale', locale === 'tr' ? 'tr_TR' : 'en_US');
    upsertMeta('property', 'og:locale:alternate', locale === 'tr' ? 'en_US' : 'tr_TR');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:site', DEFAULT_TWITTER_HANDLE);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', DEFAULT_OG_IMAGE);

    upsertLink('canonical', canonical);
    upsertLink('alternate', canonical, { hreflang: 'x-default' });
    upsertLink('alternate', canonical, { hreflang: 'en' });
    upsertLink('alternate', canonical, { hreflang: 'tr' });
  }, [title, description, keywords, canonical, locale, noIndex, page]);

  return null;
}
